import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import ServiceEntryGrid from '../components/ServiceEntryGrid';
import ServicesPreview from '../components/ServicesPreview';
import AutocompleteInput from '../components/AutocompleteInput';
import { fetchMasterCategories, fetchMasterServices, addMasterService } from '../utils/masterCatalog';
import {
  rowsFromServiceCategories,
  buildServiceCategoriesFromRows,
  validateRows,
  findDuplicateRowErrors,
  groupRowsByCategory,
  isRowEmpty,
  makeEmptyCombo,
  combosFromServicesData,
  buildCombosForPublish,
  validateCombos,
  validateGenderMatch
} from '../utils/serviceRows';
import { DURATIONS, DEFAULT_DURATION, GENDERS, DEFAULT_GENDER } from '../constants/masterData';
import './SalonServicesEditor.css';

const AUTOSAVE_DELAY = 1500;

function SalonServicesEditor({ salonId: salonIdProp, salonName: salonNameProp, requestId, requestType }) {
  const { salonId: salonIdParam } = useParams();
  const salonId = salonIdProp || salonIdParam;
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [combos, setCombos] = useState([]);
  const [salonName, setSalonName] = useState(salonNameProp || '');
  const [servicesFor, setServicesFor] = useState('Unisex'); // salon's profile.servicesFor — gates validateGenderMatch
  const [masterCategories, setMasterCategories] = useState([]);
  const [masterServices, setMasterServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [saveState, setSaveState] = useState('loaded'); // loaded | dirty | saving | saved | error
  const [lastPublishedAt, setLastPublishedAt] = useState(null);
  const skipNextAutosave = useRef(true);
  const saveTimer = useRef(null);
  const latestRows = useRef(rows);
  const latestCombos = useRef(combos);

  const [view, setView] = useState('edit'); // 'edit' | 'preview'
  const [validationErrors, setValidationErrors] = useState([]);
  const [publishAttempted, setPublishAttempted] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Add-to-master modal
  const [masterModal, setMasterModal] = useState(null); // { name, categoryId, gender, defaultDuration, targetRowId }
  const [savingMaster, setSavingMaster] = useState(false);

  // Combo modal: working copy of the combo being added/edited
  const [comboModal, setComboModal] = useState(null); // { combo, isNew }
  const [comboSearch, setComboSearch] = useState('');

  // Stale-draft detection: baseSnapshot is the salon's live service list as it
  // was when this draft was last synced (imported / re-imported / published).
  // If the live list drifts from it, the salon edited services from the mobile
  // app and publishing this draft would overwrite those changes.
  const baseSnapshotRef = useRef(null);
  const [conflict, setConflict] = useState(false);
  const [conflictDismissed, setConflictDismissed] = useState(false);
  const [reimporting, setReimporting] = useState(false);

  // Normalize a live servicesData structure (categories + combos) for
  // comparison — tolerates legacy field variants and ordering; round-trip
  // stable with publish output
  const snapshotOf = (servicesData, order) =>
    JSON.stringify({
      categories: buildServiceCategoriesFromRows(
        rowsFromServiceCategories(servicesData?.serviceCategories),
        order
      ),
      combos: buildCombosForPublish(combosFromServicesData(servicesData?.combos))
    });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setLoadError('');
        const [salonSnap, draftSnap, categories, services] = await Promise.all([
          getDoc(doc(db, 'salons', salonId)),
          getDoc(doc(db, 'salonServiceDrafts', salonId)),
          fetchMasterCategories(),
          fetchMasterServices()
        ]);
        if (cancelled) return;

        if (!salonSnap.exists()) {
          setLoadError('Salon not found');
          return;
        }
        const salonData = salonSnap.data();
        setSalonName(salonData.profile?.shopName || 'Unknown Salon');
        setServicesFor(salonData.profile?.servicesFor || 'Unisex');
        setMasterCategories(categories);
        setMasterServices(services);

        const liveServicesData = salonData.profile?.servicesData || {};
        const catOrder = categories.map((c) => c.name);
        const liveSnapshot = snapshotOf(liveServicesData, catOrder);

        let initialRows = [];
        let initialCombos = [];
        let usedDraft = false;
        if (draftSnap.exists() && Array.isArray(draftSnap.data().rows)) {
          initialRows = draftSnap.data().rows;
          initialCombos = Array.isArray(draftSnap.data().combos)
            ? draftSnap.data().combos
            : combosFromServicesData(liveServicesData.combos); // older drafts predate combos
          usedDraft = true;
          setLastPublishedAt(draftSnap.data().lastPublishedAt || null);
          baseSnapshotRef.current = draftSnap.data().baseSnapshot || null;
          // The salon changed its live list since this draft was last synced.
          // Drafts saved before combos existed have a categories-only baseline
          // (a JSON array) — compare against that format too so they don't
          // false-alarm when nothing actually changed.
          const legacyLiveSnapshot = JSON.stringify(
            buildServiceCategoriesFromRows(
              rowsFromServiceCategories(liveServicesData.serviceCategories),
              catOrder
            )
          );
          if (
            baseSnapshotRef.current &&
            baseSnapshotRef.current !== liveSnapshot &&
            baseSnapshotRef.current !== legacyLiveSnapshot
          ) {
            setConflict(true);
          }
        }

        // Use the salon's live list when there is no draft, or when the draft
        // is effectively empty while the salon has live services (e.g. the
        // draft was cleared, or the owner added services from the mobile app).
        // The editor should always open showing the salon's current services.
        const draftHasContent =
          initialRows.some((r) => !isRowEmpty(r)) ||
          initialCombos.some((c) => (c.title || '').trim());
        if (!usedDraft || !draftHasContent) {
          const liveRows = rowsFromServiceCategories(liveServicesData.serviceCategories);
          const liveCombos = combosFromServicesData(liveServicesData.combos);
          if (!usedDraft || liveRows.length > 0 || liveCombos.length > 0) {
            initialRows = liveRows;
            initialCombos = liveCombos;
            baseSnapshotRef.current = liveSnapshot;
            setConflict(false);
          }
        }
        skipNextAutosave.current = true;
        setRows(groupRowsByCategory(initialRows, categories.map((c) => c.name)));
        setCombos(initialCombos);
      } catch (err) {
        if (!cancelled) setLoadError('Failed to load. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [salonId]);

  const saveDraft = useCallback(async (rowsToSave, combosToSave) => {
    try {
      setSaveState('saving');
      await setDoc(
        doc(db, 'salonServiceDrafts', salonId),
        {
          salonId,
          rows: rowsToSave,
          combos: combosToSave,
          baseSnapshot: baseSnapshotRef.current || null,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.email || ''
        },
        { merge: true }
      );
      setSaveState('saved');
    } catch (err) {
      setSaveState('error');
    }
  }, [salonId]);

  // Debounced autosave on row/combo changes
  useEffect(() => {
    latestRows.current = rows;
    latestCombos.current = combos;
    if (skipNextAutosave.current) {
      skipNextAutosave.current = false;
      return undefined;
    }
    setSaveState('dirty');
    saveTimer.current = setTimeout(() => saveDraft(rows, combos), AUTOSAVE_DELAY);
    return () => clearTimeout(saveTimer.current);
  }, [rows, combos, saveDraft]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (saveState === 'dirty' || saveState === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveState]);

  // Live validation highlighting after the first publish attempt
  useEffect(() => {
    if (publishAttempted) {
      setValidationErrors([...validateRows(rows), ...validateGenderMatch(rows, servicesFor), ...validateCombos(combos)]);
    }
  }, [rows, combos, servicesFor, publishAttempted]);

  const categoryOrder = useMemo(() => masterCategories.map((c) => c.name), [masterCategories]);

  // Keep same-category rows together; the grid renders a header per category section
  const handleRowsChange = useCallback(
    (next) => setRows(groupRowsByCategory(next, categoryOrder)),
    [categoryOrder]
  );

  // Duplicates are highlighted live; the full required-field validation only
  // kicks in once a publish has been attempted
  const liveDupErrors = useMemo(() => findDuplicateRowErrors(rows), [rows]);
  const displayErrors = publishAttempted ? validationErrors : liveDupErrors;

  const errorFields = useMemo(() => {
    const map = {};
    displayErrors.forEach((err) => {
      if (!err.rowId) return; // combo errors don't map to grid cells
      if (!map[err.rowId]) map[err.rowId] = new Set();
      map[err.rowId].add(err.field);
    });
    return map;
  }, [displayErrors]);

  const previewCategories = useMemo(
    () => buildServiceCategoriesFromRows(rows, categoryOrder),
    [rows, categoryOrder]
  );

  const previewCombos = useMemo(() => buildCombosForPublish(combos), [combos]);

  // Search-and-select source for combo included services: everything already
  // entered in the grid, any category
  const comboSearchOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    rows.forEach((r) => {
      if (isRowEmpty(r) || !(r.service || '').trim()) return;
      const name = `${r.service.trim()}${(r.subService || '').trim() ? ` - ${r.subService.trim()}` : ''}`;
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      options.push({
        id: r.id,
        name,
        nameLower: key,
        categoryName: r.category,
        gender: r.gender,
        defaultDuration: r.duration,
        price: Number(r.price) || null
      });
    });
    return options;
  }, [rows]);

  const publishSummary = useMemo(() => {
    const services = previewCategories.flatMap((c) => c.services || []);
    const withSubs = services.filter((s) => s.subServices?.length);
    const pricedItems = services.reduce(
      (sum, s) => sum + (s.subServices?.length || 1),
      0
    );
    return {
      categories: previewCategories.length,
      services: services.length,
      withSubs: withSubs.length,
      pricedItems,
      combos: previewCombos.length
    };
  }, [previewCategories, previewCombos]);

  const handlePublishClick = () => {
    setPublishAttempted(true);
    const errors = [...validateRows(rows), ...validateGenderMatch(rows, servicesFor), ...validateCombos(combos)];
    setValidationErrors(errors);
    if (errors.length) return;
    if (rows.every(isRowEmpty) && previewCombos.length === 0) {
      alert('Nothing to submit — the service list is empty.');
      return;
    }
    setShowPublishModal(true);
  };

  // Flatten a serviceCategories tree into category>>service (or
  // category>>service>>subService) keyed price/duration pairs, for diffing
  // the new draft against a baseline.
  const flattenCategoriesToMap = (categories) => {
    const map = new Map();
    (categories || []).forEach((cat) => {
      const catName = cat.name || cat.categoryName || '';
      (cat.services || cat.serviceList || []).forEach((svc) => {
        if (Array.isArray(svc.subServices) && svc.subServices.length) {
          svc.subServices.forEach((sub) => {
            map.set(`${catName}>>${svc.name}>>${sub.name}`, {
              price: Number(sub.price) || 0,
              duration: sub.duration ?? null
            });
          });
        } else {
          map.set(`${catName}>>${svc.name}`, {
            price: Number(svc.price) || 0,
            duration: svc.duration ?? null
          });
        }
      });
    });
    return map;
  };

  // Stamp enabled:true on every service and subService object — the salon
  // toggles these off during review before publishing.
  const stampEnabled = (categories) =>
    (categories || []).map((cat) => ({
      ...cat,
      services: (cat.services || []).map((svc) =>
        Array.isArray(svc.subServices) && svc.subServices.length
          ? { ...svc, subServices: svc.subServices.map((sub) => ({ ...sub, enabled: true })) }
          : { ...svc, enabled: true }
      )
    }));

  const handlePublishConfirm = async () => {
    try {
      setPublishing(true);
      // Flush any pending autosave first so draft and published stay in sync
      clearTimeout(saveTimer.current);
      await saveDraft(latestRows.current, latestCombos.current);

      const salonRef = doc(db, 'salons', salonId);
      const salonSnap = await getDoc(salonRef);
      const existingServicesData = salonSnap.data()?.profile?.servicesData || {};
      const publishedCategories = buildServiceCategoriesFromRows(latestRows.current, categoryOrder);
      const publishedCombos = buildCombosForPublish(latestCombos.current);

      // Diff against the salon's current live list (only meaningful for
      // 'update' requests — a fresh initial_setup has nothing to compare to)
      const baselineCategories = requestId && requestType === 'update' ? existingServicesData.serviceCategories : [];
      const baselineMap = flattenCategoriesToMap(baselineCategories);
      const draftMap = flattenCategoriesToMap(publishedCategories);
      let addedCount = 0;
      let updatedCount = 0;
      draftMap.forEach((value, key) => {
        const base = baselineMap.get(key);
        if (!base) {
          addedCount += 1;
        } else if (base.price !== value.price || base.duration !== value.duration) {
          updatedCount += 1;
        }
      });
      const categoriesCount = publishedCategories.length;

      await updateDoc(salonRef, {
        'profile.pendingServicesData': {
          serviceCategories: stampEnabled(publishedCategories),
          combos: publishedCombos
        },
        updatedAt: serverTimestamp()
      });

      // The draft now matches what was submitted — reset the sync baseline
      baseSnapshotRef.current = snapshotOf(
        { serviceCategories: publishedCategories, combos: publishedCombos },
        categoryOrder
      );
      await updateDoc(doc(db, 'salonServiceDrafts', salonId), {
        lastPublishedAt: serverTimestamp(),
        baseSnapshot: baseSnapshotRef.current
      });

      if (requestId) {
        await updateDoc(doc(db, 'serviceRequests', requestId), {
          status: 'submitted_for_review',
          submittedForReviewAt: serverTimestamp(),
          preparedBy: auth.currentUser?.email || '',
          summary: { addedCount, updatedCount, categoriesCount }
        });
        await updateDoc(salonRef, { serviceCatalogStatus: 'awaiting_review' });
      }

      setConflict(false);
      setConflictDismissed(false);
      setLastPublishedAt({ toDate: () => new Date() });
      setShowPublishModal(false);
      alert(`Draft submitted for ${salonName} to review and publish.`);
      if (requestId) {
        navigate('/service-requests');
      }
    } catch (err) {
      alert(`Failed to submit: ${err.message}`);
    } finally {
      setPublishing(false);
    }
  };

  // Replace the draft with the salon's CURRENT live service list (fetched
  // fresh, in case it changed while this page was open). Used by the header
  // "Sync" button and the stale-draft conflict banner.
  const handleReimport = async () => {
    const confirmed = window.confirm(
      "Load the salon's current live service list into the editor?\n\nAny unpublished draft edits will be discarded."
    );
    if (!confirmed) return;

    try {
      setReimporting(true);
      const salonSnap = await getDoc(doc(db, 'salons', salonId));
      const liveServicesData = salonSnap.data()?.profile?.servicesData || {};
      baseSnapshotRef.current = snapshotOf(liveServicesData, categoryOrder);
      // State changes trigger autosave, which persists rows/combos + new baseline
      setRows(groupRowsByCategory(rowsFromServiceCategories(liveServicesData.serviceCategories), categoryOrder));
      setCombos(combosFromServicesData(liveServicesData.combos));
      setConflict(false);
      setConflictDismissed(false);
      setPublishAttempted(false);
      setValidationErrors([]);
    } catch (err) {
      alert(`Failed to re-import: ${err.message}`);
    } finally {
      setReimporting(false);
    }
  };

  // ----- Combos -----

  const openComboModal = (combo) => {
    setComboSearch('');
    if (combo) {
      // Deep copy so Cancel discards edits
      setComboModal({
        combo: { ...combo, services: combo.services.map((s) => ({ ...s })) },
        isNew: false
      });
    } else {
      setComboModal({ combo: makeEmptyCombo(), isNew: true });
    }
  };

  const updateModalCombo = (patch) => {
    setComboModal((m) => ({ ...m, combo: { ...m.combo, ...patch } }));
  };

  const addComboService = (name, price) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setComboModal((m) => {
      const exists = m.combo.services.some((s) => s.name.toLowerCase() === trimmed.toLowerCase());
      if (exists) {
        alert(`"${trimmed}" is already included in this combo`);
        return m;
      }
      return { ...m, combo: { ...m.combo, services: [...m.combo.services, { name: trimmed, price: price ?? null }] } };
    });
    setComboSearch('');
  };

  const handleComboSave = () => {
    const combo = comboModal.combo;
    if (!(combo.title || '').trim()) {
      alert('Combo name is required');
      return;
    }
    if (combo.services.filter((s) => (s.name || '').trim()).length < 2) {
      alert('Include at least 2 services in the combo');
      return;
    }
    if (!(Number(combo.totalPrice) > 0)) {
      alert('Combo price must be greater than 0');
      return;
    }
    const duplicate = combos.find(
      (c) => c.id !== combo.id && (c.title || '').trim().toLowerCase() === combo.title.trim().toLowerCase()
    );
    if (duplicate) {
      alert(`A combo named "${duplicate.title}" already exists for this salon`);
      return;
    }
    setCombos((prev) =>
      comboModal.isNew ? [...prev, combo] : prev.map((c) => (c.id === combo.id ? combo : c))
    );
    setComboModal(null);
  };

  const handleComboDelete = (combo) => {
    if (!window.confirm(`Remove combo "${combo.title || 'Untitled'}" from this salon?`)) return;
    setCombos((prev) => prev.filter((c) => c.id !== combo.id));
  };

  // ----- Add to master catalog -----

  const openMasterModal = (name, targetRowId) => {
    setMasterModal({
      name,
      categoryId: masterCategories.find((c) => c.enabled)?.id || '',
      gender: DEFAULT_GENDER,
      defaultDuration: DEFAULT_DURATION,
      targetRowId
    });
  };

  const handleMasterModalSave = async () => {
    const name = masterModal.name.trim();
    if (!name) {
      alert('Service name is required');
      return;
    }
    if (!masterModal.categoryId) {
      alert('Please select a category');
      return;
    }
    const duplicate = masterServices.find((s) => (s.nameLower || '') === name.toLowerCase());
    if (duplicate) {
      alert(`"${duplicate.name}" already exists in the master catalog`);
      return;
    }

    try {
      setSavingMaster(true);
      const category = masterCategories.find((c) => c.id === masterModal.categoryId);
      const payload = {
        name,
        categoryId: masterModal.categoryId,
        categoryName: category?.name || '',
        gender: masterModal.gender,
        defaultDuration: masterModal.defaultDuration,
        subServices: []
      };
      const newId = await addMasterService(payload);
      const newService = { id: newId, ...payload, nameLower: name.toLowerCase(), enabled: true };
      setMasterServices((prev) => [...prev, newService].sort((a, b) => a.name.localeCompare(b.name)));

      // Autofill the row that triggered the modal (and re-group by category)
      setRows((prev) =>
        groupRowsByCategory(
          prev.map((r) =>
            r.id === masterModal.targetRowId
              ? {
                  ...r,
                  service: name,
                  category: payload.categoryName,
                  gender: payload.gender,
                  duration: payload.defaultDuration,
                  masterServiceId: newId
                }
              : r
          ),
          categoryOrder
        )
      );
      setMasterModal(null);
    } catch (err) {
      alert(`Failed to add to master catalog: ${err.message}`);
    } finally {
      setSavingMaster(false);
    }
  };

  const saveStatusLabel = {
    loaded: '',
    dirty: 'Unsaved changes…',
    saving: 'Saving…',
    saved: 'All changes saved ✓',
    error: 'Save failed'
  }[saveState];

  const formatPublished = (ts) => {
    if (!ts) return null;
    try {
      const date = ts.toDate ? ts.toDate() : new Date(ts);
      return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="salon-services-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading services editor...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="salon-services-page">
        <div className="error-container">
          <p className="error-text">{loadError}</p>
          <button className="retry-btn" onClick={() => navigate(requestId ? '/service-requests' : '/salons')}>
            {requestId ? 'Back to Requests' : 'Back to Salons'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="salon-services-page">
      <div className="sse-header">
        <div className="sse-header-info">
          <button
            className="sse-back-btn"
            onClick={() => navigate(requestId ? '/service-requests' : '/salons')}
          >
            {requestId ? '← Requests' : '← Salons'}
          </button>
          <div>
            <h2>{salonName}</h2>
            <p className="sse-subtitle">
              {requestId ? 'Preparing a draft for the salon to review — nothing goes live yet' : 'Service list editor'}
              {lastPublishedAt && formatPublished(lastPublishedAt) && (
                <span className="sse-published-at"> · Last saved: {formatPublished(lastPublishedAt)}</span>
              )}
            </p>
          </div>
        </div>
        <div className="sse-header-actions">
          <span className={`sse-save-status ${saveState}`}>
            {saveStatusLabel}
            {saveState === 'error' && (
              <button className="sse-retry-save" onClick={() => saveDraft(latestRows.current, latestCombos.current)}>Retry</button>
            )}
          </span>
          <button
            className="sse-sync-btn"
            onClick={handleReimport}
            disabled={reimporting || publishing}
            title="Load the salon's current live services into the editor"
          >
            {reimporting ? 'Syncing...' : '⟳ Sync salon services'}
          </button>
          <div className="sse-view-toggle">
            <button
              className={`tab-btn ${view === 'edit' ? 'active' : ''}`}
              onClick={() => setView('edit')}
            >
              ✏️ Edit
            </button>
            <button
              className={`tab-btn ${view === 'preview' ? 'active' : ''}`}
              onClick={() => setView('preview')}
            >
              👁 Preview
            </button>
          </div>
          <button className="sse-publish-btn" onClick={handlePublishClick} disabled={publishing || saveState === 'saving'}>
            {publishing ? 'Submitting...' : 'Submit for Review'}
          </button>
        </div>
      </div>

      {conflict && !conflictDismissed && (
        <div className="sse-conflict-banner">
          <div className="sse-conflict-text">
            ⚠ <strong>The salon changed its services after this draft was last synced</strong> —
            likely edits made from the mobile app. Publishing this draft will overwrite those changes.
          </div>
          <div className="sse-conflict-actions">
            <button className="sse-conflict-reimport-btn" onClick={handleReimport} disabled={reimporting}>
              {reimporting ? 'Importing...' : "Re-import salon's current list"}
            </button>
            <button className="sse-conflict-keep-btn" onClick={() => setConflictDismissed(true)} disabled={reimporting}>
              Keep my draft
            </button>
          </div>
        </div>
      )}

      {masterServices.length === 0 && (
        <div className="sse-info-banner">
          The master catalog is empty, so autocomplete has no suggestions yet.{' '}
          <button className="sse-link-btn" onClick={() => navigate('/master-catalog')}>
            Set up the master catalog
          </button>{' '}
          to speed up entry.
        </div>
      )}

      {displayErrors.length > 0 && (
        <div className="sse-errors-panel">
          <h4>
            {publishAttempted
              ? `Fix ${displayErrors.length} issue${displayErrors.length === 1 ? '' : 's'} before publishing:`
              : `${displayErrors.length} duplicate row${displayErrors.length === 1 ? '' : 's'} found — remove before publishing:`}
          </h4>
          <ul>
            {displayErrors.slice(0, 10).map((err, i) => (
              <li key={`${err.rowId}-${err.field}-${i}`}>
                <button
                  className="sse-error-link"
                  onClick={() => {
                    setView('edit');
                    if (err.comboId) {
                      const combo = combos.find((c) => c.id === err.comboId);
                      if (combo) openComboModal(combo);
                    } else {
                      gridRef.current?.focusCell(err.rowId, err.field);
                    }
                  }}
                >
                  {err.message}
                </button>
              </li>
            ))}
            {displayErrors.length > 10 && <li className="sse-error-more">…and {displayErrors.length - 10} more</li>}
          </ul>
        </div>
      )}

      <div style={{ display: view === 'edit' ? 'block' : 'none' }}>
        <ServiceEntryGrid
          ref={gridRef}
          rows={rows}
          onRowsChange={handleRowsChange}
          masterServices={masterServices}
          masterCategories={masterCategories}
          errorFields={errorFields}
          onAddToMaster={openMasterModal}
        />

        {/* Combos */}
        <div className="sse-combos-section">
          <div className="sse-combos-header">
            <h3>Combos ({combos.length})</h3>
            <button className="sse-add-combo-btn" onClick={() => openComboModal(null)}>
              + Add Combo
            </button>
          </div>
          {combos.length === 0 ? (
            <p className="sse-combos-empty">
              No combos yet — bundle services at a package price (e.g. Haircut + Beard Trim + Facial).
            </p>
          ) : (
            <div className="sse-combo-cards">
              {combos.map((combo) => (
                <div key={combo.id} className="sse-combo-card">
                  <div className="sse-combo-card-main">
                    <span className="sse-combo-title">{combo.title || 'Untitled combo'}</span>
                    <span className="sse-combo-services">
                      {(combo.services || []).map((s) => s.name).filter(Boolean).join(' + ') || 'No services yet'}
                    </span>
                  </div>
                  <span className="sse-combo-price">
                    {Number(combo.totalPrice) > 0 ? `₹${combo.totalPrice}` : '—'}
                  </span>
                  <div className="sse-combo-actions">
                    <button className="sse-combo-edit-btn" onClick={() => openComboModal(combo)}>Edit</button>
                    <button className="sse-combo-delete-btn" onClick={() => handleComboDelete(combo)}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: view === 'preview' ? 'block' : 'none' }}>
        <ServicesPreview serviceCategories={previewCategories} combos={previewCombos} />
      </div>

      {/* Publish confirm modal */}
      {showPublishModal && (
        <div className="modal-overlay" onClick={() => !publishing && setShowPublishModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Submit this draft to {salonName} for review?</h3>
              <button className="modal-close" onClick={() => setShowPublishModal(false)} disabled={publishing}>×</button>
            </div>
            <div className="modal-body">
              <div className="sse-publish-summary">
                <div className="sse-summary-item">
                  <span className="sse-summary-number">{publishSummary.categories}</span>
                  <span className="sse-summary-label">Categories</span>
                </div>
                <div className="sse-summary-item">
                  <span className="sse-summary-number">{publishSummary.services}</span>
                  <span className="sse-summary-label">Services</span>
                </div>
                <div className="sse-summary-item">
                  <span className="sse-summary-number">{publishSummary.withSubs}</span>
                  <span className="sse-summary-label">With sub services</span>
                </div>
                <div className="sse-summary-item">
                  <span className="sse-summary-number">{publishSummary.pricedItems}</span>
                  <span className="sse-summary-label">Priced items</span>
                </div>
                <div className="sse-summary-item">
                  <span className="sse-summary-number">{publishSummary.combos}</span>
                  <span className="sse-summary-label">Combos</span>
                </div>
              </div>
              <p className="sse-publish-note">
                This saves a draft categorized service list and combos for {salonName} to review.
                Nothing changes for customers until the salon reviews and publishes it themselves.
              </p>
              {conflict && (
                <p className="sse-publish-conflict-note">
                  ⚠ The salon has edited its services since this draft was last synced —
                  submitting will base the draft on this editor's version, not their latest changes.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={() => setShowPublishModal(false)} disabled={publishing}>
                Cancel
              </button>
              <button className="sse-publish-btn" onClick={handlePublishConfirm} disabled={publishing}>
                {publishing ? 'Submitting...' : 'Confirm & Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Combo add/edit modal */}
      {comboModal && (
        <div className="modal-overlay" onClick={() => setComboModal(null)}>
          <div className="modal-content sse-combo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{comboModal.isNew ? 'Add Combo' : 'Edit Combo'}</h3>
              <button className="modal-close" onClick={() => setComboModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="sse-modal-row">
                <div className="sse-modal-field sse-combo-name-field">
                  <label>Combo Name *</label>
                  <input
                    type="text"
                    className="sse-modal-input"
                    placeholder="e.g. Grooming Package"
                    value={comboModal.combo.title}
                    onChange={(e) => updateModalCombo({ title: e.target.value })}
                    autoFocus
                  />
                </div>
                <div className="sse-modal-field">
                  <label>Combo Price (₹) *</label>
                  <input
                    type="number"
                    min="0"
                    className="sse-modal-input"
                    placeholder="0"
                    value={comboModal.combo.totalPrice ?? ''}
                    onChange={(e) =>
                      updateModalCombo({ totalPrice: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                </div>
              </div>

              <label>Included Services * (minimum 2)</label>
              <AutocompleteInput
                className="sse-modal-input"
                value={comboSearch}
                placeholder="🔍 Search the salon's services (any category)..."
                options={comboSearchOptions}
                createLabelText="as a custom item"
                onChange={setComboSearch}
                onSelect={(opt) => addComboService(opt.name, opt.price)}
                onCreateNew={(text) => addComboService(text, null)}
              />

              {comboModal.combo.services.length === 0 ? (
                <p className="sse-modal-hint">
                  Search and select from the services entered above, or type any name and add it as a
                  custom item.
                </p>
              ) : (
                <div className="sse-combo-included-list">
                  {comboModal.combo.services.map((svc, index) => (
                    <div key={`${svc.name}-${index}`} className="sse-combo-included-row">
                      <span className="sse-combo-included-name">{svc.name}</span>
                      <input
                        type="number"
                        min="0"
                        className="sse-combo-included-price"
                        placeholder="₹ (optional)"
                        title="Individual price shown in the combo breakdown (optional)"
                        value={svc.price ?? ''}
                        onChange={(e) =>
                          updateModalCombo({
                            services: comboModal.combo.services.map((s, i) =>
                              i === index
                                ? { ...s, price: e.target.value === '' ? null : Number(e.target.value) }
                                : s
                            )
                          })
                        }
                      />
                      <button
                        className="sse-combo-included-remove"
                        onClick={() =>
                          updateModalCombo({
                            services: comboModal.combo.services.filter((_, i) => i !== index)
                          })
                        }
                        title="Remove from combo"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {comboModal.combo.services.length > 0 && (
                    <div className="sse-combo-worth">
                      Individual total: ₹
                      {comboModal.combo.services.reduce((sum, s) => sum + (Number(s.price) || 0), 0)}
                      {Number(comboModal.combo.totalPrice) > 0 && (
                        <> · Combo price: ₹{comboModal.combo.totalPrice}</>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={() => setComboModal(null)}>
                Cancel
              </button>
              <button
                className="sse-publish-btn"
                onClick={handleComboSave}
                disabled={!comboModal.combo.title.trim() || comboModal.combo.services.length < 2}
              >
                {comboModal.isNew ? 'Add Combo' : 'Save Combo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add-to-master modal */}
      {masterModal && (
        <div className="modal-overlay" onClick={() => !savingMaster && setMasterModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add to Master Catalog</h3>
              <button className="modal-close" onClick={() => setMasterModal(null)} disabled={savingMaster}>×</button>
            </div>
            <div className="modal-body">
              <label>Service Name *</label>
              <input
                type="text"
                className="sse-modal-input"
                value={masterModal.name}
                onChange={(e) => setMasterModal({ ...masterModal, name: e.target.value })}
                disabled={savingMaster}
                autoFocus
              />
              <label>Category *</label>
              <select
                className="sse-modal-input"
                value={masterModal.categoryId}
                onChange={(e) => setMasterModal({ ...masterModal, categoryId: e.target.value })}
                disabled={savingMaster}
              >
                <option value="">Select category</option>
                {masterCategories.filter((c) => c.enabled).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <div className="sse-modal-row">
                <div className="sse-modal-field">
                  <label>Gender</label>
                  <select
                    className="sse-modal-input"
                    value={masterModal.gender}
                    onChange={(e) => setMasterModal({ ...masterModal, gender: e.target.value })}
                    disabled={savingMaster}
                  >
                    {GENDERS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                <div className="sse-modal-field">
                  <label>Default Duration</label>
                  <select
                    className="sse-modal-input"
                    value={masterModal.defaultDuration}
                    onChange={(e) => setMasterModal({ ...masterModal, defaultDuration: Number(e.target.value) })}
                    disabled={savingMaster}
                  >
                    {DURATIONS.map((d) => (
                      <option key={d} value={d}>{d} mins</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="sse-modal-hint">
                This service will be suggested for every salon from now on. Sub services can be added later
                from the Master Catalog page.
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={() => setMasterModal(null)} disabled={savingMaster}>
                Cancel
              </button>
              <button
                className="sse-publish-btn"
                onClick={handleMasterModalSave}
                disabled={savingMaster || !masterModal.name.trim() || !masterModal.categoryId}
              >
                {savingMaster ? 'Saving...' : 'Add Service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalonServicesEditor;
